'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ConfirmDialog,
  InquiryFlow,
  type FlowDocument,
  type NavigationFooterAction,
  type NavigationRequest,
} from '@miso/design-system';
import type {
  AdditionalSpaceObservingSession,
  RoomObservingSession,
  SketchingSessionRoom,
  VisitCounselView,
} from '@/entities/visit-counsel/model/visit-counsel.types';
import {
  SERVICE_SET_VALUES,
  type MovingItemRequest,
  type ServiceSet,
} from '@/entities/visit-counsel/model/room-observing.types';
import { sortAdditionalSpaceObservingSessions } from '@/features/visit-counsel/counsel-progress/model/additionalSpaceObservingOrder';
import { SPACE_TYPE_LABELS } from '@/features/visit-counsel/counsel-progress/model/counsel-progress.constants';
import {
  formatRoomObservingLeafPrefix,
  formatSketchingRoomLabelSpaced,
  resolveRoomObservingDisplayLabelSpaced,
} from '@/entities/visit-counsel/model/sketchRoomDisplayLabel';
import { isSingletonRoomType } from '@/entities/visit-counsel/model/recoverRoomType';
import type { RoomTypeValue } from '@/entities/visit-counsel/model/house-sketch.types';
import {
  roomTypeFromSketchRoom,
  roomTypeFromSpaceType,
} from '@/features/visit-counsel/moving-items-flow/model/resolveObservingRoomType';
import { MovingItemsFlowAdapter } from '@/features/visit-counsel/moving-items-flow';
import {
  allEntriesDone,
  buildObservingPickerSections,
  countEntries,
  entriesToDoneInstances,
  type ObservingPickerEntry,
  type ObservingPickerSection,
} from '../model/buildObservingPickerSections';
import { usePendingObservingSessionIds } from '../model/pendingObservingSessions';
import { EditTargetSelector, type EditTarget } from './EditTargetSelector';
import { EditObservingFlow } from './EditObservingFlow';
import { RoomEditFlowAdapter } from './RoomEditFlowAdapter';

// ── Constants ────────────────────────────────────────────────────────────────

const PICK_QUESTION_ID = 'roomToRecord';
const PICK_STEP_ID = 'pick';
const TERMINAL_ID = 'rooms-record-done';
const ROOT_FLOW_ID = 'room-observing-pick-flow';

// ── 헬퍼: entry → MovingItemsFlowAdapter target 변환 ─────────────────────────

interface SelectedTarget {
  sessionId: string;
  isAdditionalSpaceMoving: boolean;
  isFirstStart: boolean;
  roomLabel: string;
  roomType?: RoomTypeValue;
  initialMovingItems: MovingItemRequest[] | undefined;
}

function toMovingItemRequests(
  session: RoomObservingSession | AdditionalSpaceObservingSession
): MovingItemRequest[] | undefined {
  const items = session.movingItems;
  if (!items || items.length === 0) return undefined;
  const mapped = items
    .filter((it) => SERVICE_SET_VALUES.includes(it.serviceSet as ServiceSet))
    .map((it) => ({
      specId: it.specId,
      attributes: it.attributes ?? {},
      serviceSet: it.serviceSet as ServiceSet,
    }));
  return mapped.length > 0 ? mapped : undefined;
}

function entryToTarget(
  entry: ObservingPickerEntry,
  sketchRooms: SketchingSessionRoom[]
): SelectedTarget | null {
  if (entry.kind === 'sketch') {
    if (!entry.session) return null;
    // picker 카드(`entry.title`)와 동일한 단독 정책을 짐 기록 화면 타이틀에도 그대로 적용한다.
    // 그렇지 않으면 카드에는 "거실" 로 보이지만 진입 후 "1번 거실에서 가져가실 짐을…" 처럼
    // ordinal prefix 가 다시 새어나오는 불일치가 생긴다.
    const singleton = isSingletonRoomType(entry.room, sketchRooms);
    const labelOpts = singleton ? ({ singleton: true } as const) : undefined;
    const labelFromApi = resolveRoomObservingDisplayLabelSpaced(
      entry.session,
      sketchRooms,
      labelOpts
    );
    const roomLabel =
      labelFromApi ??
      formatRoomObservingLeafPrefix(
        entry.session,
        sketchRooms,
        { index: entry.room.index, namePrefix: entry.room.namePrefix },
        labelOpts
      ) ??
      formatSketchingRoomLabelSpaced(entry.room, labelOpts);
    return {
      sessionId: entry.session.id,
      isAdditionalSpaceMoving: false,
      isFirstStart: entry.session.status === 'REQUESTED',
      roomLabel,
      roomType: roomTypeFromSketchRoom(entry.room),
      initialMovingItems: toMovingItemRequests(entry.session),
    };
  }
  // linked-additional (sketching 룸과 같은 물리적 공간) 은 sketching 라벨을 우선 노출,
  // unlinked (운반 경로 등) 은 spaceType 라벨을 사용한다.
  // linked-additional 도 단독이면 ordinal 을 떼고 노출 (picker 카드 정책과 일치).
  let roomLabel: string;
  if (entry.linkedSketchRoom) {
    const singleton = isSingletonRoomType(entry.linkedSketchRoom, sketchRooms);
    roomLabel = formatSketchingRoomLabelSpaced(
      entry.linkedSketchRoom,
      singleton ? { singleton: true } : undefined
    );
  } else {
    roomLabel = SPACE_TYPE_LABELS[entry.session.spaceType] ?? entry.session.spaceType;
  }
  return {
    sessionId: entry.sessionId,
    isAdditionalSpaceMoving: true,
    isFirstStart: entry.session.status === 'REQUESTED',
    roomLabel,
    roomType: entry.linkedSketchRoom
      ? roomTypeFromSketchRoom(entry.linkedSketchRoom)
      : roomTypeFromSpaceType(entry.session.spaceType),
    initialMovingItems: toMovingItemRequests(entry.session),
  };
}

// ── FlowDocument 빌더 ────────────────────────────────────────────────────────

interface BuildFlowOptions {
  sections: ObservingPickerSection[];
  /**
   * 현재 백그라운드 후처리(예: 영상 반영 폴링) 가 진행 중인 sessionId 집합.
   * 매칭되는 entry 는 `pending: true` 로 picker 에 전달되어 spinner + dimmed 처리된다.
   */
  pendingSessionIds: ReadonlySet<string>;
}

function buildPickFlow({ sections, pendingSessionIds }: BuildFlowOptions): FlowDocument {
  return {
    schemaVersion: 1,
    id: ROOT_FLOW_ID,
    initial: PICK_STEP_ID,
    steps: {
      [PICK_STEP_ID]: {
        kind: 'questions',
        questions: [
          {
            id: PICK_QUESTION_ID,
            layout: 'L2',
            title: '기록할 방을 선택해주세요.',
            input: {
              kind: 'item-picker',
              required: false,
              multiple: false,
              mode: 'navigation',
              requireAllItems: true,
              reviewTitle: '기록할 방',
              reviewButtonLabel: '완료',
              sections: sections.map((sec) => ({
                id: sec.id,
                title: sec.title,
                items: sec.entries.map((e) => ({
                  id: e.itemId,
                  title: e.title,
                  icon: e.iconEmoji,
                  ...(e.description ? { description: e.description } : {}),
                  // entry.sessionId 가 pending set 에 있으면 picker 가 dimmed + spinner.
                  // sketch entry 의 sessionId 는 nullable 이라 falsy 면 자동 제외된다.
                  ...(e.sessionId && pendingSessionIds.has(e.sessionId)
                    ? { pending: true as const }
                    : {}),
                })),
              })),
            },
          },
        ],
        next: { kind: 'terminal', target: TERMINAL_ID },
      },
    },
    terminals: {
      [TERMINAL_ID]: { kind: 'placeholder-auto' },
    },
  };
}

// ── Adapter ─────────────────────────────────────────────────────────────────

export interface RoomObservingFlowAdapterProps {
  visitCounselId: string;
  counsel: VisitCounselView;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  /** 모든 방 완료 시 — page 가 stage 재평가로 다음 단계 진입 */
  onAfterDone: () => void;
  /** 사용자 취소(뒤로가기) — page 가 fallback */
  onCancel: () => void;
}

/**
 * 사용자가 다음 진행할 방을 직접 선택하는 어댑터.
 *
 * 흐름:
 *   1. counsel 스냅샷 → buildObservingPickerSections 으로 "주요공간/방/추가공간" 분류.
 *   2. InquiryFlow + item-picker(navigation 모드) 로 그리드 표시. 완료된 방은
 *      done instance 로 prefill 되어 카운트 badge 가 노출된다.
 *   3. 카드 클릭 시:
 *        - 미완료 → 즉시 nested MovingItemsFlowAdapter 진입
 *        - 완료   → "이미 기록되었어요. 편집하시겠어요?" 모달 → 확인 시 nested 어댑터(편집)
 *   4. nested 어댑터의 onAfterDone → refetch + setSelectedTarget(null) → 다시 picker 표시.
 *      모든 방이 완료되면 useEffect 가 onAfterDone 을 호출해 page stage 가 done 으로 전환.
 *
 * 현관(ENTRANCE)은 picker 에서 제외된다 — `resolveCounselFlowStage` 가 현관을 우선 자동
 * 진행시키므로, 본 어댑터는 항상 "현관 이후" 방들에 대한 순서 선택을 다룬다.
 */
export function RoomObservingFlowAdapter({
  visitCounselId,
  counsel,
  showToast,
  refetch,
  onAfterDone,
  onCancel,
}: RoomObservingFlowAdapterProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [selected, setSelected] = useState<SelectedTarget | null>(null);
  const [editConfirmEntry, setEditConfirmEntry] = useState<ObservingPickerEntry | null>(null);
  // 완료된 방 편집 흐름: ConfirmDialog 확인 → EditTargetSelector → EditObservingFlow
  const [editTargetEntry, setEditTargetEntry] = useState<ObservingPickerEntry | null>(null);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  // "방 수정하기" 모드 — picker 하단 보조 액션이 켜고, RoomEditFlowAdapter 가 표시된다.
  const [editingRooms, setEditingRooms] = useState(false);

  const sketchRooms = counsel.sessions?.sketching?.rooms ?? [];
  const roomObserving = counsel.sessions?.roomObserving ?? [];
  const additionalSpaceObserving = useMemo(
    () => sortAdditionalSpaceObservingSessions(counsel.sessions?.additionalSpaceObserving ?? []),
    [counsel.sessions?.additionalSpaceObserving]
  );

  const sections = useMemo(
    () =>
      buildObservingPickerSections({
        rooms: sketchRooms,
        roomObserving,
        additionalSpaceObserving,
      }),
    [sketchRooms, roomObserving, additionalSpaceObserving]
  );

  // 백그라운드 폴링 중인 세션 — picker 카드의 spinner/dimmed 표시에 사용된다.
  // 어댑터의 mount 와 무관하게 글로벌 atom 으로 유지되므로, picker → 다른 화면 →
  // 다시 picker 로 복귀하더라도 폴링이 끝나기 전까지 spinner 가 계속 보인다.
  const pendingSessionIds = usePendingObservingSessionIds();

  const { total, done } = countEntries(sections);

  /**
   * 모든 entry 가 완료되면 onAfterDone 으로 전환 — page 가 stage 를 done 으로 재평가.
   * nested 어댑터가 종료되어 selected===null 인 시점에만 실행한다.
   */
  const allDone = sections.length > 0 && allEntriesDone(sections);
  const fireDoneRef = useRef(false);
  useEffect(() => {
    if (selected) return;
    if (!allDone) {
      fireDoneRef.current = false;
      return;
    }
    if (fireDoneRef.current) return;
    fireDoneRef.current = true;
    onAfterDone();
  }, [selected, allDone, onAfterDone]);

  const flow = useMemo(
    () => buildPickFlow({ sections, pendingSessionIds }),
    [sections, pendingSessionIds]
  );

  const initialValues = useMemo(
    () => ({ [PICK_QUESTION_ID]: entriesToDoneInstances(sections) }),
    [sections]
  );

  /**
   * InquiryFlow 의 reducer 는 lazy initializer 라 첫 마운트에만 `initialValues`
   * 를 적용한다. 폴링이 끝나서 새 done entry 가 생기더라도 InquiryFlow 내부
   * accumulated state 가 갱신되지 않아 ItemPicker 의 ✓ 체크 배지가 즉시
   * 반영되지 않는 문제가 있다.
   *
   * "어떤 entry 가 done 인가" 의 시그니처가 변할 때만 InquiryFlow 를 강제로
   * remount 시켜 새 initialValues 가 적용되도록 한다 — done set 이 그대로면
   * key 도 그대로라 picker 가 깜박이지 않는다.
   */
  const doneSignature = useMemo(() => {
    const ids: string[] = [];
    for (const sec of sections) for (const e of sec.entries) if (e.done) ids.push(e.itemId);
    ids.sort();
    return ids.join('|');
  }, [sections]);

  // ── navigation handler ────────────────────────────────────────────────────
  const entryById = useMemo(() => {
    const map = new Map<string, ObservingPickerEntry>();
    for (const sec of sections) for (const e of sec.entries) map.set(e.itemId, e);
    return map;
  }, [sections]);

  const enterTargetForEntry = useCallback(
    (entry: ObservingPickerEntry) => {
      const target = entryToTarget(entry, sketchRooms);
      if (!target) {
        showToast('해당 방의 세션 정보를 찾을 수 없어요.', 'error');
        return;
      }
      setSelected(target);
    },
    [sketchRooms, showToast]
  );

  const handleNavigation = useCallback(
    ({ item }: NavigationRequest) => {
      const entry = entryById.get(item.id);
      if (!entry) return;
      if (entry.done) {
        setEditConfirmEntry(entry);
        return;
      }
      enterTargetForEntry(entry);
    },
    [entryById, enterTargetForEntry]
  );

  const handleEditConfirm = useCallback(() => {
    if (!editConfirmEntry) return;
    const entry = editConfirmEntry;
    setEditConfirmEntry(null);
    // 확인 후 편집 대상 선택 화면으로 진입 (짐만/영상만/둘 다)
    setEditTargetEntry(entry);
    setEditTarget(null);
  }, [editConfirmEntry]);

  const handleEditCancel = useCallback(() => {
    setEditConfirmEntry(null);
  }, []);

  // ── nested adapter callbacks ─────────────────────────────────────────────
  const handleNestedAfterDone = useCallback(async (_nextCounsel: VisitCounselView) => {
    // 한 방 완료 후 picker 로 복귀. counsel 은 이미 nested 어댑터 내부에서
    // refetch 되어 본 어댑터의 props 도 곧 갱신된다. 즉시 selected 만 null 로
    // 돌려 picker UI 를 다시 보여준다.
    setSelected(null);
  }, []);

  const handleNestedCancel = useCallback(() => {
    setSelected(null);
  }, []);

  // ── 방 수정하기 footer 보조 액션 ──────────────────────────────────────────
  /**
   * picker CTA(완료) 아래에 노출되는 "방 수정하기" 텍스트 버튼.
   * 클릭 시 `RoomEditFlowAdapter` 로 진입한다 — 사용자는 거기서 추가/삭제 후
   * picker 로 복귀한다 (counsel prop 갱신으로 sections 자동 재계산).
   *
   * sections 가 비어있는 경우 (예: 모든 방 삭제 후) 에도 노출이 의미 있다 —
   * 다만 picker 자체는 sections.length === 0 시 placeholder 만 그리므로,
   * 본 버튼은 sections.length > 0 일 때만 picker UI 와 함께 보인다.
   */
  const navigationFooterAction: NavigationFooterAction = useMemo(
    () => ({
      label: '방 수정하기',
      onPress: () => setEditingRooms(true),
    }),
    []
  );

  // ── 렌더 ──────────────────────────────────────────────────────────────────
  if (editingRooms) {
    return (
      <RoomEditFlowAdapter
        visitCounselId={visitCounselId}
        counsel={counsel}
        showToast={showToast}
        refetch={refetch}
        onBack={() => setEditingRooms(false)}
      />
    );
  }

  if (selected) {
    return (
      <MovingItemsFlowAdapter
        key={selected.sessionId}
        visitCounselId={visitCounselId}
        sessionId={selected.sessionId}
        isAdditionalSpaceMoving={selected.isAdditionalSpaceMoving}
        isFirstStart={selected.isFirstStart}
        roomLabel={selected.roomLabel}
        roomType={selected.roomType}
        initialMovingItems={selected.initialMovingItems}
        showToast={showToast}
        refetch={refetch}
        onAfterDone={handleNestedAfterDone}
        onCancel={handleNestedCancel}
      />
    );
  }

  // 완료된 방 편집: editTargetEntry 가 있으면 EditTargetSelector 또는 EditObservingFlow 표시.
  if (editTargetEntry) {
    const editMoveTarget = entryToTarget(editTargetEntry, sketchRooms);

    const exitEditFlow = () => {
      setEditTargetEntry(null);
      setEditTarget(null);
    };

    if (!editTarget || !editMoveTarget) {
      // 편집 대상 선택 화면 (짐만 / 영상만 / 둘 다).
      // editMoveTarget 이 null 인 경우(세션 미매칭)도 여기서 처리 — 뒤로가기로 복귀 가능.
      return (
        <EditTargetSelector
          roomLabel={editTargetEntry.title}
          onSelect={(target) => setEditTarget(target)}
          onBack={exitEditFlow}
        />
      );
    }

    return (
      <EditObservingFlow
        key={`${editMoveTarget.sessionId}-${editTarget}`}
        visitCounselId={visitCounselId}
        sessionId={editMoveTarget.sessionId}
        isAdditionalSpaceMoving={editMoveTarget.isAdditionalSpaceMoving}
        roomLabel={editMoveTarget.roomLabel}
        roomType={editMoveTarget.roomType}
        initialMovingItems={editMoveTarget.initialMovingItems}
        editTarget={editTarget}
        showToast={showToast}
        refetch={refetch}
        onAfterDone={(counsel) => {
          exitEditFlow();
          handleNestedAfterDone(counsel);
        }}
        onBack={() => setEditTarget(null)}
        container={wrapperRef.current}
      />
    );
  }

  // 모든 entry 가 완료된 직후 — useEffect 가 onAfterDone 을 호출하기 전 placeholder.
  if (sections.length === 0 || allDone) {
    return (
      <div ref={wrapperRef} style={{ position: 'relative', height: '100%', overflow: 'hidden' }} />
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <InquiryFlow
        key={doneSignature}
        flow={flow}
        initialValues={initialValues}
        registries={{ terminals: {} }}
        onComplete={() => {
          // navigation 모드에서 "완료" CTA — requireAllItems=true 로 모든 카드가 채워진
          // 시점에만 호출된다. 이미 done=true 이므로 onAfterDone 으로 전환.
          onAfterDone();
        }}
        onCancel={onCancel}
        onError={(err) => {
          showToast(err.message ?? '진행 오류가 발생했습니다', 'error');
          onCancel();
        }}
        container={wrapperRef.current}
        onRequestNavigation={handleNavigation}
        navigationFooterAction={navigationFooterAction}
      />

      <ConfirmDialog
        open={!!editConfirmEntry}
        title="이미 기록한 방이에요"
        description={`${editConfirmEntry?.title ?? ''}은(는) 이미 짐 기록과 영상 촬영이 완료되었어요.\n다시 기록하면 기존 내용이 수정됩니다. 계속할까요?`}
        confirmLabel="편집하기"
        onConfirm={handleEditConfirm}
        onCancel={handleEditCancel}
        container={wrapperRef.current}
      />

      {/* 진행 카운트는 InquiryFlow 의 requireAllItems progress bar 가 자동 표기.
          (어댑터 디버깅용으로 외부 노출이 필요하면 여기서 별도 노출 가능) */}
      <span data-progress-total={total} data-progress-done={done} hidden />
    </div>
  );
}

export default RoomObservingFlowAdapter;
